import { useCallback } from 'react';
import { t, Trans } from '@lingui/macro';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Grid,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTypedParams } from 'react-router-typesafe-routes/dom';
import { ArrowLeft } from 'tabler-icons-react';

import { DescriptionList } from '../../components/description-list/description-list';
import { TimeBlock } from '../../components/train-route-info/train-route-info';
import { UiCard } from '../../components/ui/ui-card/ui-card';
import { useOrders } from '../../domains/cabinet/features/orders/orders.provider';
import { RefundTicketSection } from '../../domains/cabinet/features/tickets/refund-ticket-button';
import { EOrderStatus, TOrder } from '../../domains/orders/core/order';
import { UserTimeZoneNotify } from '../../domains/tz-notify/tz-notify';
import { MainSideMenu } from '../../features/main-side-menu/main-side-menu';
import { getNameLocal } from '../../lib/local/get-title';
import { useLocal } from '../../lib/local/use-local';
import { routeCabinet } from '../../router/routes';

import styles from './order-details.module.css';

export const OrderDetailsPage = observer(() => {
  const { getOrdersRequest, ordersStore } = useOrders();
  const [lang] = useLocal();
  const navigate = useNavigate();
  const { id } = useTypedParams(routeCabinet.ORDER);
  const isLoading = getOrdersRequest.isWaitingForLoad;

  const payment = ordersStore.getOrderById(id || 0);
  const ticket = ordersStore.getMainTicketByOrderId(id || 0);

  const descriptionList = [
    { label: t`Номер платежа`, value: payment?.id ? `# ${payment?.id}` : '' },
    {
      label: t`Дата заказа`,
      value: new Date(payment?.date || 0).toLocaleString(),
    },
    { label: t`Описание`, value: payment?.description },

    {
      label: t`Стоимость билетов`,
      value: payment?.paySumm ? `${payment?.paySumm} ֏` : '',
    },
    {
      label: t`Оплачено`,
      value: payment?.paySumm ? `${payment?.paySumm} ֏` : '',
    },
  ];

  const handleOrderUpdate = useCallback(
    (order: TOrder) => {
      ordersStore.updateOrder(order);
    },
    [ordersStore]
  );

  if (isLoading)
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.backButton}>
            <ActionIcon
              variant="transparent"
              radius="50%"
              size="40px"
              bg="transparent"
              onClick={() => navigate(routeCabinet.path)}
            >
              <ArrowLeft size={24} strokeWidth={2} color="white" />
            </ActionIcon>
          </div>

          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные заказа</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>

        <main className={styles.main}>
          <UiCard>
            <Box pos="relative" mih={150}>
              <LoadingOverlay visible />
            </Box>
          </UiCard>
        </main>
      </div>
    );

  if (!id)
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.backButton}>
            <ActionIcon
              variant="transparent"
              radius="50%"
              size="40px"
              bg="transparent"
              onClick={() => navigate(routeCabinet.path)}
            >
              <ArrowLeft size={24} strokeWidth={2} color="white" />
            </ActionIcon>
          </div>

          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные заказа</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>
        <main className={styles.main}>
          <Stack spacing="sm">
            <UiCard>
              <Trans>
                Отсутствует ID платежа. Купленные билеты можно посмотреть в
                разделе "Мои билеты"
              </Trans>
            </UiCard>
            <UiCard>
              <Button
                size="lg"
                fullWidth
                onClick={() => navigate(routeCabinet.TICKETS.path)}
              >
                <Trans>Мои билеты</Trans>
              </Button>
            </UiCard>
          </Stack>
        </main>
      </div>
    );

  if (!isLoading && !payment) {
    return (
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.backButton}>
            <ActionIcon
              variant="transparent"
              radius="50%"
              size="40px"
              bg="transparent"
              onClick={() => navigate(routeCabinet.path)}
            >
              <ArrowLeft size={24} strokeWidth={2} color="white" />
            </ActionIcon>
          </div>
          <div className={styles.routeTitle}>
            <h1 className={styles.headerTitle}>
              <Trans>Данные заказа</Trans>
            </h1>
          </div>

          <div className={styles.mainSideMenu}>
            <MainSideMenu />
          </div>
        </header>
        <main className={styles.main}>
          <Stack spacing="sm">
            <UiCard>
              <Trans>
                Заказ не найден. Попробуйте обновить страницу или вернуться к
                списку заказов
              </Trans>
            </UiCard>

            <UiCard>
              <Button
                size="lg"
                fullWidth
                onClick={() => navigate(routeCabinet.ORDERS.path)}
              >
                <Trans>Мои заказы</Trans>
              </Button>
            </UiCard>
          </Stack>
        </main>
      </div>
    );
  }

  const isTicketWaiting = payment?.status === EOrderStatus.new;
  const isTicketDone = payment?.status === EOrderStatus.success;
  const isTicketError = payment?.status === EOrderStatus.error;
  const isTicketBooked = payment?.status === EOrderStatus.booked;
  const isTicketCanceled = payment?.status === EOrderStatus.canceled;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.backButton}>
          <ActionIcon
            variant="transparent"
            radius="50%"
            size="40px"
            bg="transparent"
            onClick={() => navigate(routeCabinet.path)}
          >
            <ArrowLeft size={24} strokeWidth={2} color="white" />
          </ActionIcon>
        </div>

        <div className={styles.routeTitle}>
          <h1 className={styles.headerTitle}>
            <Trans>Данные заказа</Trans>
          </h1>
        </div>

        <div className={styles.mainSideMenu}>
          <MainSideMenu />
        </div>
      </header>
      <main className={styles.main}>
        <Stack spacing="sm">
          <UserTimeZoneNotify />

          <UiCard>
            {payment?.tickets.map((ticket) =>
              ticket.mainTicketId == undefined ? (
                <Grid align="center" justify="center" gutter={10}>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, ticket.stationFrom)}
                      date={new Date(ticket.departureTime)}
                    />
                  </Grid.Col>
                  <Grid.Col span="auto">
                    <Divider my={4} />
                  </Grid.Col>
                  <Grid.Col span="content">
                    <TimeBlock
                      target={getNameLocal(lang, ticket.stationTo)}
                      date={new Date(ticket.arrivalTime)}
                      direction="end"
                    />
                  </Grid.Col>
                </Grid>
              ) : null
            )}
          </UiCard>
          <UiCard>
            <DescriptionList list={descriptionList} />
          </UiCard>

          {isTicketError && (
            <UiCard>
              <Text>
                <Trans>При обработке платежа произошла ошибка</Trans>
              </Text>
            </UiCard>
          )}

          {isTicketCanceled && (
            <>
              <UiCard>
                <Text>
                  <Trans>
                    Заказ был отменен. Сумма заказа вернется на ваш счет
                  </Trans>
                </Text>
              </UiCard>

              <UiCard>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate(routeCabinet.TICKETS.path)}
                  fullWidth
                >
                  <Trans>Мои билеты</Trans>
                </Button>
              </UiCard>
            </>
          )}

          {isTicketBooked && (
            <UiCard>
              <Text>
                <Trans>Заказ был забронирован и ожидает оплаты</Trans>
              </Text>
            </UiCard>
          )}

          {isTicketWaiting && (
            <>
              <UiCard>
                <Text>
                  <Trans>
                    Заказ обрабатывается, ожидаем подтверждения оплаты
                  </Trans>
                  .
                  <br />
                  <Trans>
                    Если у вас не получилось произвести оплату, оплатить заказ
                    можно по ссылке
                  </Trans>
                  : <a href={payment.payConfirmationUrl}>Перейти к оплате</a>
                  <br />
                  <Trans>
                    Попробуйте обновить статус, либо проверить купленные билеты
                    в разделе "Мои билеты"
                  </Trans>
                  .
                </Text>
              </UiCard>

              <UiCard>
                <Stack spacing="sm">
                  <Button
                    size="lg"
                    onClick={() => window.location.reload()}
                    fullWidth
                  >
                    <Trans>Обновить статус</Trans>
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate(routeCabinet.TICKETS.path)}
                    fullWidth
                  >
                    <Trans>Мои билеты</Trans>
                  </Button>
                </Stack>
              </UiCard>
            </>
          )}

          {isTicketDone && (
            <>
              <UiCard>
                <Button
                  // variant="default"
                  size="lg"
                  onClick={() =>
                    navigate(routeCabinet.TICKET.buildPath({ id: payment.id }))
                  }
                  fullWidth
                >
                  <Trans>Показать билет</Trans>
                </Button>
              </UiCard>
            </>
          )}

          {payment && (
            <UiCard>
              <RefundTicketSection
                order={payment}
                ticket={ticket}
                onOrderUpdate={handleOrderUpdate}
              />
            </UiCard>
          )}
        </Stack>
      </main>
    </div>
  );
});
